/**
 * OutdoorAreaForm — Editor form for ramp and tiedown areas
 *
 * Simplified form with no height, walls, or doors.
 * Supports border color/style and optional floor fill.
 */

import { useEffect, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  useBuildingEditorStore,
  type OutdoorRectangularFormValues,
} from '@/stores/buildingEditorStore'
import type {
  AreaPlacementFieldsInput,
  AreaCategory,
  PlacementPattern,
  PlacementProgression,
  LaneCoupling,
  PitchMode,
  AutoSort,
  TurnoverMode,
  AreaParkingMode,
} from '@/types/area-placement'
import { AutoPlacementSection } from './AutoPlacementSection'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Save, X } from 'lucide-react'
import type { PricingFormFields } from '@/lib/schemas/pricing-fields'
import { PricingFeaturesSection } from './PricingFeaturesSection'
import { pricingFieldsSchema } from '@/lib/schemas/pricing-fields'
import { RemoveAreaButton } from '../shared/RemoveAreaButton'

// ============================================================================
// FORM SCHEMA
// ============================================================================

/** Optional number that treats '' / null as undefined (avoids coercing to 0). */
const optionalNumber = z.preprocess(
  (v) => (v === '' || v === null || v === undefined ? undefined : v),
  z.coerce.number().optional()
)

const outdoorFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  code: z.string().min(1, 'Area code is required').max(20),
  area_subtype: z.enum(['ramp_area', 'tiedown_area', 'exit_point', 'fuel_depot']),
  width_ft: z.coerce.number().min(2, 'Min 2 ft').max(5000, 'Max 5000 ft'),
  depth_ft: z.coerce.number().min(2, 'Min 2 ft').max(5000, 'Max 5000 ft'),
  show_floor: z.boolean(),
  floor_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  floor_opacity: z.coerce.number().min(0).max(100),
  border_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  border_style: z.enum(['solid', 'dashed']),
  border_width_ft: z.coerce.number().min(0.5, 'Min 0.5 ft').max(10, 'Max 10 ft'),
  bearing_deg: z.coerce.number(),
  // Pricing fields (optional)
  ...pricingFieldsSchema.shape,
  // Event auto-placement fields (optional; assembled into AreaPlacementFieldsInput)
  ap_auto_placement: z.boolean().optional(),
  ap_area_category: z.string().optional(),
  ap_turnover_mode: z.string().optional(),
  ap_parking_mode: z.string().optional(),
  ap_tow_min_wingspan_ft: optionalNumber,
  ap_dwell_max_minutes: optionalNumber,
  ap_fill_priority: optionalNumber,
  ap_pattern: z.string().optional(),
  ap_active_from: z.string().optional(),
  ap_active_until: z.string().optional(),
  ap_release_from: z.string().optional(),
  ap_release_until: z.string().optional(),
  ap_placement_heading: optionalNumber,
  ap_progression: z.string().optional(),
  ap_lanes: optionalNumber,
  ap_coupling: z.string().optional(),
  ap_pitch_mode: z.string().optional(),
  ap_safety_margin_ft: optionalNumber,
  ap_auto_sort: z.string().optional(),
  ap_taxiway_exit_point_id: z.string().optional(),
  ap_fallback_area_id: z.string().optional(),
  ap_enforcement: z.string().optional(),
  ap_max_wingspan_ft: optionalNumber,
  ap_max_length_ft: optionalNumber,
  ap_allowed_categories: z.array(z.string()).optional(),
  ap_allowed_client_ids: z.array(z.string()).optional(),
  // Exit-area only: aircraft per departure slot this exit can push
  exit_throughput: optionalNumber,
}).superRefine((v, ctx) => {
  // A tow wingspan threshold only makes sense in 'both' mode (size decides
  // self-park vs tow). For 'both', the value is optional — null means "use the
  // planner default threshold" (matches the column comment).
  if (v.ap_parking_mode === 'both' && v.ap_tow_min_wingspan_ft == null) {
    // optional: leaving it blank falls back to the planner default — no error.
  } else if (v.ap_parking_mode !== 'both' && v.ap_tow_min_wingspan_ft != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ap_tow_min_wingspan_ft'],
      message: 'Tow threshold only applies when parking mode is "Both"',
    })
  }
})

type OutdoorFormData = z.infer<typeof outdoorFormSchema>

/** datetime-local input needs 'YYYY-MM-DDTHH:mm'; trim an ISO timestamp to it. */
const toLocalInput = (iso?: string | null): string =>
  iso ? iso.slice(0, 16) : ''
const strOrNull = (v?: string): string | null => (v && v.length ? v : null)

// ============================================================================
// COMPONENT
// ============================================================================

interface OutdoorAreaFormProps {
  onSave: (
    values: OutdoorRectangularFormValues,
    pricing?: PricingFormFields,
    placement?: AreaPlacementFieldsInput
  ) => Promise<void>
  onCancel: () => void
  isCreating: boolean
  isSaving: boolean
  isPlaced?: boolean
  // Edit mode props
  areaId?: string
  areaName?: string
  onRemoved?: () => void
  initialPricing?: Partial<PricingFormFields>
  initialPlacement?: AreaPlacementFieldsInput | null
}

export function OutdoorAreaForm({
  onSave,
  onCancel,
  isCreating,
  isSaving,
  isPlaced = true,
  areaId,
  areaName,
  onRemoved,
  initialPricing,
  initialPlacement,
}: OutdoorAreaFormProps) {
  const { outdoorFormValues, updateOutdoorFormValues } = useBuildingEditorStore()
  const [subTab, setSubTab] = useState<'area' | 'placement'>('area')

  const defaultCategory: AreaCategory =
    (outdoorFormValues?.area_subtype || 'ramp_area') === 'tiedown_area' ? 'tiedown' : 'ramp'

  const form = useForm<OutdoorFormData>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(outdoorFormSchema) as unknown as Resolver<OutdoorFormData>,
    defaultValues: {
      name: outdoorFormValues?.name || 'New Ramp Area',
      code: outdoorFormValues?.code || '',
      area_subtype: outdoorFormValues?.area_subtype || 'ramp_area',
      width_ft: outdoorFormValues?.width_ft || 200,
      depth_ft: outdoorFormValues?.depth_ft || 150,
      show_floor: outdoorFormValues?.floor_color !== null,
      floor_color: outdoorFormValues?.floor_color || '#FFD700',
      floor_opacity: (outdoorFormValues?.floor_opacity ?? 0.3) * 100,
      border_color: outdoorFormValues?.border_color || '#FFD700',
      border_style: outdoorFormValues?.border_style || 'solid',
      border_width_ft: outdoorFormValues?.border_width_ft ?? 2,
      bearing_deg: outdoorFormValues?.bearing_deg || 0,
      // Pricing defaults
      pricing_model: initialPricing?.pricing_model || 'fixed',
      monthly_rate_private: initialPricing?.monthly_rate_private ?? undefined,
      monthly_rate_shared: initialPricing?.monthly_rate_shared ?? undefined,
      transient_rate_nightly: initialPricing?.transient_rate_nightly ?? undefined,
      rate_per_sqft_private: initialPricing?.rate_per_sqft_private ?? undefined,
      rate_per_sqft_shared: initialPricing?.rate_per_sqft_shared ?? undefined,
      construction_year: initialPricing?.construction_year ?? undefined,
      fire_suppression: initialPricing?.fire_suppression ?? false,
      security_system: initialPricing?.security_system ?? false,
      // Auto-placement defaults (seeded from the loaded area when editing)
      ap_auto_placement: initialPlacement?.auto_placement ?? false,
      ap_area_category: initialPlacement?.area_category ?? defaultCategory,
      ap_turnover_mode: initialPlacement?.turnover_mode ?? 'mixed',
      ap_parking_mode: initialPlacement?.parking_mode ?? 'both',
      ap_tow_min_wingspan_ft: initialPlacement?.tow_min_wingspan_ft ?? undefined,
      ap_dwell_max_minutes: initialPlacement?.eligibility?.dwell?.max_minutes ?? undefined,
      ap_fill_priority: initialPlacement?.fill_priority ?? undefined,
      ap_pattern: initialPlacement?.pattern ?? '',
      ap_active_from: toLocalInput(initialPlacement?.active_from),
      ap_active_until: toLocalInput(initialPlacement?.active_until),
      ap_release_from: toLocalInput(initialPlacement?.release_from),
      ap_release_until: toLocalInput(initialPlacement?.release_until),
      ap_placement_heading: initialPlacement?.placement_heading ?? 0,
      ap_progression: initialPlacement?.progression ?? 'side_by_side',
      ap_lanes: initialPlacement?.lanes ?? 1,
      ap_coupling: initialPlacement?.coupling ?? '',
      ap_pitch_mode: initialPlacement?.pitch_mode ?? 'variable',
      ap_safety_margin_ft: initialPlacement?.safety_margin_ft ?? undefined,
      ap_auto_sort: initialPlacement?.auto_sort ?? 'balanced',
      ap_taxiway_exit_point_id: initialPlacement?.taxiway_exit_point_id ?? '',
      ap_fallback_area_id: initialPlacement?.fallback_area_id ?? '',
      ap_enforcement: initialPlacement?.eligibility?.enforcement ?? 'hard',
      ap_max_wingspan_ft: initialPlacement?.eligibility?.size?.max_wingspan_ft ?? undefined,
      ap_max_length_ft: initialPlacement?.eligibility?.size?.max_length_ft ?? undefined,
      ap_allowed_categories: initialPlacement?.eligibility?.types?.allowed_categories ?? [],
      ap_allowed_client_ids: initialPlacement?.eligibility?.clients?.allowed_client_ids ?? [],
      exit_throughput: initialPlacement?.exit_throughput ?? undefined,
    },
  })

  // Sync form changes to store (for preview updates)
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (!values) return
      updateOutdoorFormValues({
        name: values.name || '',
        code: values.code || '',
        area_subtype: values.area_subtype as 'ramp_area' | 'tiedown_area' | 'exit_point' | 'fuel_depot',
        width_ft: Number(values.width_ft) || 200,
        depth_ft: Number(values.depth_ft) || 150,
        border_color: values.border_color || '#FFD700',
        border_style: values.border_style as 'solid' | 'dashed',
        border_width_ft: Number(values.border_width_ft) || 2,
        floor_color: values.show_floor ? (values.floor_color || '#FFD700') : null,
        floor_opacity: (Number(values.floor_opacity) || 30) / 100,
        bearing_deg: Number(values.bearing_deg) || 0,
      })
    })
    return () => subscription.unsubscribe()
  }, [form, updateOutdoorFormValues])

  // Sync bearing from store (rotation ring changes)
  useEffect(() => {
    if (outdoorFormValues?.bearing_deg !== undefined) {
      const currentBearing = form.getValues('bearing_deg')
      if (Math.abs(currentBearing - outdoorFormValues.bearing_deg) > 0.5) {
        form.setValue('bearing_deg', Math.round(outdoorFormValues.bearing_deg))
      }
    }
  }, [outdoorFormValues?.bearing_deg, form])

  const handleSubmit = async (data: OutdoorFormData) => {
    const outdoorValues: OutdoorRectangularFormValues = {
      name: data.name,
      code: data.code,
      area_subtype: data.area_subtype,
      width_ft: data.width_ft,
      depth_ft: data.depth_ft,
      border_color: data.border_color,
      border_style: data.border_style,
      border_width_ft: data.border_width_ft,
      floor_color: data.show_floor ? data.floor_color : null,
      floor_opacity: data.floor_opacity / 100,
      bearing_deg: data.bearing_deg,
      latitude: outdoorFormValues?.latitude || 0,
      longitude: outdoorFormValues?.longitude || 0,
    }

    const pricing: PricingFormFields = {
      pricing_model: data.pricing_model,
      monthly_rate_private: data.monthly_rate_private ?? null,
      monthly_rate_shared: data.monthly_rate_shared ?? null,
      transient_rate_nightly: data.transient_rate_nightly ?? null,
      rate_per_sqft_private: data.rate_per_sqft_private ?? null,
      rate_per_sqft_shared: data.rate_per_sqft_shared ?? null,
      construction_year: data.construction_year ?? null,
      fire_suppression: data.fire_suppression ?? false,
      security_system: data.security_system ?? false,
    }

    // Exit areas: minimal placement payload (category + exit vector). No
    // auto-placement rules — exits are non-allocatable infrastructure.
    if (data.area_subtype === 'exit_point') {
      const exitPlacement: AreaPlacementFieldsInput = {
        area_category: 'exit',
        auto_placement: false,
        exit_heading: data.bearing_deg,
        exit_throughput: data.exit_throughput ?? null,
      }
      await onSave(outdoorValues, pricing, exitPlacement)
      return
    }

    // Fuel depot: minimal payload — non-allocatable infrastructure the event
    // planner reads to anchor fuel-truck dispatch on real pavement.
    if (data.area_subtype === 'fuel_depot') {
      const depotPlacement: AreaPlacementFieldsInput = {
        area_category: 'fuel_depot',
        auto_placement: false,
      }
      await onSave(outdoorValues, pricing, depotPlacement)
      return
    }

    const lanes = data.ap_lanes ?? 1
    const placement: AreaPlacementFieldsInput = {
      auto_placement: !!data.ap_auto_placement,
      area_category: (data.ap_area_category as AreaCategory) || null,
      turnover_mode: (data.ap_turnover_mode as TurnoverMode) || null,
      parking_mode: (data.ap_parking_mode as AreaParkingMode) || null,
      // Only meaningful in 'both' mode; null elsewhere (= planner default).
      tow_min_wingspan_ft:
        data.ap_parking_mode === 'both' ? (data.ap_tow_min_wingspan_ft ?? null) : null,
      fill_priority: data.ap_fill_priority ?? null,
      pattern: (data.ap_pattern as PlacementPattern) || null,
      active_from: data.ap_active_from || null,
      active_until: data.ap_active_until || null,
      release_from: data.ap_release_from || null,
      release_until: data.ap_release_until || null,
      placement_heading: data.ap_placement_heading ?? null,
      progression: (data.ap_progression as PlacementProgression) || null,
      lanes,
      coupling: lanes === 2 ? ((data.ap_coupling as LaneCoupling) || null) : null,
      pitch_mode: (data.ap_pitch_mode as PitchMode) || null,
      safety_margin_ft: data.ap_safety_margin_ft ?? null,
      auto_sort: (data.ap_auto_sort as AutoSort) || null,
      taxiway_exit_point_id: strOrNull(data.ap_taxiway_exit_point_id),
      fallback_area_id: strOrNull(data.ap_fallback_area_id),
      eligibility: {
        enforcement: (data.ap_enforcement as 'hard' | 'soft') || 'hard',
        size: {
          max_wingspan_ft: data.ap_max_wingspan_ft ?? null,
          max_length_ft: data.ap_max_length_ft ?? null,
        },
        types: {
          allowed_categories: data.ap_allowed_categories ?? [],
        },
        clients: {
          allowed_client_ids: data.ap_allowed_client_ids ?? [],
        },
        dwell: {
          max_minutes: data.ap_dwell_max_minutes ?? null,
        },
      },
    }

    await onSave(outdoorValues, pricing, placement)
  }

  const showFloor = form.watch('show_floor')
  const areaSubtype = form.watch('area_subtype')
  const isExit = areaSubtype === 'exit_point'
  const isFuelDepot = areaSubtype === 'fuel_depot'
  // Non-allocatable infrastructure (exits, fuel depots): no auto-placement rules.
  const isInfra = isExit || isFuelDepot

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex-1 flex flex-col min-h-0"
      >
        {/* Sub-tab strip: Area details vs Auto-Placement settings.
            Hidden for non-allocatable infrastructure (exits, fuel depots). */}
        {!isInfra && (
          <div className="flex border-b shrink-0">
            <button
              type="button"
              onClick={() => setSubTab('area')}
              className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                subTab === 'area'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Area
            </button>
            <button
              type="button"
              onClick={() => setSubTab('placement')}
              className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                subTab === 'placement'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Auto-Placement
            </button>
          </div>
        )}

        <ScrollArea className="flex-1 min-h-0">
          {!isInfra && subTab === 'placement' && (
            <AutoPlacementSection
              form={form as unknown as ReturnType<typeof useForm>}
            />
          )}
          <div className={`p-4 space-y-5 ${isInfra || subTab === 'area' ? '' : 'hidden'}`}>
            {/* Identity Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Identity
              </h3>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Main Ramp" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Area Code</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. RMP-001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Type Section — only in create mode */}
            {isCreating && (
              <>
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Area Type
                  </h3>
                  <FormField
                    control={form.control}
                    name="area_subtype"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="ramp_area">Ramp Area</SelectItem>
                            <SelectItem value="tiedown_area">Tiedown Area</SelectItem>
                            <SelectItem value="exit_point">Taxiway Exit / Entrance</SelectItem>
                            <SelectItem value="fuel_depot">Fuel Depot</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Separator />
              </>
            )}

            {/* Dimensions Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Dimensions
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="width_ft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Width (ft)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="depth_ft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Depth (ft)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Appearance Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Appearance
              </h3>

              {/* Floor Fill */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm">Show Floor Fill</FormLabel>
                  <FormField
                    control={form.control}
                    name="show_floor"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                {showFloor && (
                  <div className="pl-2 space-y-3 border-l-2 border-muted ml-1">
                    <FormField
                      control={form.control}
                      name="floor_color"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs min-w-[60px]">Color</FormLabel>
                            <FormControl>
                              <Input
                                type="color"
                                className="w-10 h-8 p-0.5 cursor-pointer"
                                {...field}
                              />
                            </FormControl>
                            <Input
                              className="flex-1 h-8 text-xs"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="floor_opacity"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs min-w-[60px]">Opacity</FormLabel>
                            <FormControl>
                              <Slider
                                min={0}
                                max={100}
                                step={5}
                                value={[field.value]}
                                onValueChange={([v]) => field.onChange(v)}
                                className="flex-1"
                              />
                            </FormControl>
                            <span className="text-xs text-muted-foreground w-8 text-right">
                              {field.value}%
                            </span>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Border */}
              <div className="space-y-2">
                <FormLabel className="text-sm">Border</FormLabel>
                <div className="grid grid-cols-3 gap-3">
                  <FormField
                    control={form.control}
                    name="border_color"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs">Color</FormLabel>
                          <FormControl>
                            <Input
                              type="color"
                              className="w-10 h-8 p-0.5 cursor-pointer"
                              {...field}
                            />
                          </FormControl>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="border_style"
                    render={({ field }) => (
                      <FormItem>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="solid">Solid</SelectItem>
                            <SelectItem value="dashed">Dashed</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="border_width_ft"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Width (ft)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.5" className="h-8" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Position Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Position
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Latitude</label>
                  <p className="text-sm font-mono">
                    {outdoorFormValues?.latitude?.toFixed(6) || '—'}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Longitude</label>
                  <p className="text-sm font-mono">
                    {outdoorFormValues?.longitude?.toFixed(6) || '—'}
                  </p>
                </div>
              </div>
              <FormField
                control={form.control}
                name="bearing_deg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isExit ? 'Exit heading (degrees)' : 'Rotation (degrees)'}</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    {isExit && (
                      <p className="text-xs text-muted-foreground">
                        Approach bearing onto the taxiway — the exit vector the placer aligns to.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Exit Settings — exit areas only */}
            {isExit && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Exit Settings
                  </h3>
                  <FormField
                    control={form.control}
                    name="exit_throughput"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Throughput (aircraft / departure slot)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Optional — falls back to global default"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            <Separator />

            {/* Pricing Section (collapsed) */}
            <PricingFeaturesSection form={form as unknown as ReturnType<typeof useForm>} />
          </div>
        </ScrollArea>

        {/* Footer Buttons */}
        <div className="border-t px-4 py-3 flex items-center gap-2 shrink-0">
          {!isCreating && areaId && onRemoved && (
            <RemoveAreaButton
              areaId={areaId}
              areaName={areaName || 'this area'}
              onRemoved={onRemoved}
              compact
            />
          )}
          <div className="flex-1" />
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving || !isPlaced}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving
              ? 'Saving...'
              : isCreating
                ? `Create ${
                    areaSubtype === 'ramp_area'
                      ? 'Ramp'
                      : areaSubtype === 'exit_point'
                        ? 'Exit'
                        : areaSubtype === 'fuel_depot'
                          ? 'Fuel Depot'
                          : 'Tiedown'
                  } Area`
                : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
